"use client";

import { useState, useEffect } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { Button } from "@speakai/ui/button";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@speakai/ui/dialog";
import { useTranslation } from "react-i18next";
import { useTheme } from "next-themes";
import { useToast } from "@speakai/ui/toast";
import { LoaderCircle, ShieldCheck } from "lucide-react";
import { track, trackFailure } from "@/lib/integrations/sentry-tracking";
import { getStripePromise, confirmSetupWithElements, classifyStripeError } from "@/features/subscription/payment/stripe";
import { useSaveSetupIntent } from "@/features/subscription/mutations";
import { usePaymentMethods } from "@/features/subscription/payment-queries";
import { PaymentSourceType } from "@/features/subscription/types";
import * as paymentApi from "@/features/subscription/api";
import { ConnectPaypalButton } from "./connect-paypal-button";

interface AddSourceProps {
  open: boolean;
  onClose: () => void;
  /** Fires after a card or PayPal source is saved, so callers can pre-select. */
  onSuccess: (source?: { id: string; type: "card" | "paddle" }) => void;
  canManageBilling?: boolean;
}

function OrAddCardDivider() {
  const { t } = useTranslation();
  return (
    <div className="my-4 flex items-center gap-3">
      <div className="h-px flex-1 bg-border" />
      <span className="text-xs uppercase text-muted-foreground">
        {t("PAYMENT.OR_ADD_CARD", "Or Add Card")}
      </span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

interface AddCardFormProps {
  clientSecret: string;
  onClose: () => void;
  onSuccess: (source?: { id: string; type: "card" | "paddle" }) => void;
  canManageBilling: boolean;
  canAddPaypal: boolean;
}

function AddCardForm({
  clientSecret,
  onClose,
  onSuccess,
  canManageBilling,
  canAddPaypal,
}: AddCardFormProps) {
  const { t } = useTranslation();
  const toastApi = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const saveSetupIntent = useSaveSetupIntent();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The PaymentElement iframe mounts asynchronously; submitting before its
  // `ready` event makes Stripe throw IntegrationError ("could not retrieve data
  // from the specified Element"). Gate submission on this. See SPEAK-CLIENT-A3.
  const [elementReady, setElementReady] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !elementReady) return;

    setSubmitting(true);
    setError(null);

    try {
      const setupIntent = await confirmSetupWithElements({ stripe, elements, clientSecret });

      const pm = setupIntent.payment_method;
      const pmId = typeof pm === "string" ? pm : undefined;

      const saveResult = await saveSetupIntent.mutateAsync({
        setupIntentId: setupIntent.id,
        paymentMethodId: pmId ?? "",
      });

      if (!saveResult.success) {
        setError(saveResult.error ?? t("PAYMENT.SAVE_CARD_FAILED", "Failed to save card"));
        return;
      }

      track("payment", "add_card");
      onSuccess(pmId ? { id: pmId, type: "card" } : undefined);
      onClose();
    } catch (err: unknown) {
      // Only card_error/validation_error are safe to show verbatim; integration
      // and load errors get the generic localized message so customers never see
      // Stripe's internal developer text.
      const { message, isCardError, failureType } = classifyStripeError(err);
      const userMessage =
        isCardError && message
          ? message
          : t("PAYMENT.GENERIC_ERROR", "Payment failed, please try again.");
      setError(userMessage);
      toastApi.error(t("PAYMENT.ERROR_TITLE", "Payment error"), userMessage.slice(0, 200));
      trackFailure("payment", "fail", {
        error_reason: message.slice(0, 200),
        charge_type: "add_card",
        payment_method: "card",
        failure_type: failureType,
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-1 flex-col min-h-0">
      <DialogBody>
        {canAddPaypal && (
          <>
            <ConnectPaypalButton
              canManageBilling={canManageBilling}
              onSuccess={(newSource) => {
                if (newSource) onSuccess(newSource);
              }}
              onClose={onClose}
              layout="centered"
            />
            <OrAddCardDivider />
          </>
        )}

        <div data-testid="stripe-container">
          <PaymentElement
            options={{ layout: "tabs" }}
            onReady={() => setElementReady(true)}
            onLoadError={() =>
              setError(t("PAYMENT.GENERIC_ERROR", "Payment failed, please try again."))
            }
          />
        </div>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t("PAYMENT.SECURED_BY_STRIPE", "Secured by Stripe")}
        </p>
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
          {t("ACTION.CANCEL", "Cancel")}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || !elements || !elementReady || submitting || !canManageBilling}
        >
          {submitting || !elementReady ? (
            <>
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />
              <span>{submitting ? t("ACTION.SAVING", "Saving…") : t("ACTION.LOADING", "Loading")}</span>
            </>
          ) : t("ACTION.ADD_CARD", "Add card")}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function AddSource({
  open,
  onClose,
  onSuccess,
  canManageBilling = true,
}: AddSourceProps) {
  const { t } = useTranslation();
  const { resolvedTheme } = useTheme();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const { data: stripeMethods = [] } = usePaymentMethods(
    PaymentSourceType.Stripe,
  );
  const { data: paddleMethods = [] } = usePaymentMethods(
    PaymentSourceType.Paddle,
  );
  const canAddCard = stripeMethods.length < 2;
  const canAddPaypal = paddleMethods.length < 1;
  const canAddAny = canAddCard || canAddPaypal;

  // Snapshot theme on open so the <Elements> provider doesn't remount on
  // theme toggle and destroy in-progress card entry.
  const [snapshotTheme, setSnapshotTheme] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setFetchError(null);
      setSnapshotTheme(undefined);
      return;
    }

    // Skip the SetupIntent fetch when only PayPal capacity remains.
    if (!canAddCard) {
      return;
    }

    setSnapshotTheme(resolvedTheme);

    let cancelled = false;

    async function fetchSetupIntent() {
      setIsFetching(true);
      setFetchError(null);
      try {
        const res = await paymentApi.getSetupIntent();
        if (cancelled) return;
        if (!res.success || !res.data?.clientSecret) {
          throw new Error(res.error ?? "Failed to create setup intent");
        }
        setClientSecret(res.data.clientSecret);
      } catch (err: unknown) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "Failed to load payment form";
          setFetchError(message);
        }
      } finally {
        if (!cancelled) setIsFetching(false);
      }
    }

    fetchSetupIntent();
    return () => { cancelled = true; };
    // resolvedTheme is captured once via snapshotTheme, see comment above the snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canAddCard]);

  const stripeTheme = snapshotTheme === "dark" ? "night" : "stripe";

  const showStripeSlot = canAddCard && !isFetching && !fetchError && clientSecret;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="add-source-title"
      aria-describedby="add-source-desc"
      size="lg"
    >
      <DialogHeader>
        <div className="flex flex-col gap-1 min-w-0 w-full">
          <h2 id="add-source-title" className="text-lg font-semibold">
            {t("PAYMENT.ADD_NEW_CARD", "Add a New Payment Method")}
          </h2>
          <p id="add-source-desc" className="text-xs text-muted-foreground">
            {t(
              "PAYMENT_SETTINGS.INFO.ACCEPTED_CARDS",
              "Speak accepts Visa, Mastercard, and all major credit and debit cards.",
            )}
          </p>
        </div>
      </DialogHeader>

      {!canAddAny && (
        <>
          <DialogBody>
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t(
                "PAYMENT.MAX_METHODS_REACHED",
                "You have reached the maximum number of payment methods to link with your account.",
              )}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("ACTION.CLOSE", "Close")}
            </Button>
          </DialogFooter>
        </>
      )}

      {canAddAny && canAddCard && isFetching && (
        <>
          <DialogBody>
            {canAddPaypal && (
              <ConnectPaypalButton
                canManageBilling={canManageBilling}
                onSuccess={(newSource) => {
                  if (newSource) onSuccess(newSource);
                }}
                onClose={onClose}
                layout="centered"
              />
            )}
            <div className="flex items-center justify-center px-6 py-12">
              <LoaderCircle className="h-5 w-5 animate-spin motion-reduce:animate-none text-primary" aria-hidden="true" />
              <span className="ml-2 text-sm text-muted-foreground">
                {t("PAYMENT.LOADING_FORM", "Loading secure payment form…")}
              </span>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("ACTION.CANCEL", "Cancel")}
            </Button>
          </DialogFooter>
        </>
      )}

      {canAddAny && canAddCard && fetchError && (
        <>
          <DialogBody>
            {canAddPaypal && (
              <ConnectPaypalButton
                canManageBilling={canManageBilling}
                onSuccess={(newSource) => {
                  if (newSource) onSuccess(newSource);
                }}
                onClose={onClose}
                layout="centered"
              />
            )}
            <p className="text-sm text-destructive" role="alert">
              {fetchError}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("ACTION.CANCEL", "Cancel")}
            </Button>
          </DialogFooter>
        </>
      )}

      {canAddAny && showStripeSlot && (
        <Elements
          stripe={getStripePromise()}
          options={{
            clientSecret,
            fonts: [
              {
                cssSrc: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap",
              },
            ],
            appearance: {
              theme: stripeTheme,
              variables: {
                borderRadius: "8px",
                fontFamily:
                  '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontWeightNormal: "400",
                fontWeightMedium: "500",
                fontWeightBold: "600",
                fontSizeBase: "14px",
                colorText: "var(--color-foreground)",
                colorTextPlaceholder: "var(--color-muted-foreground)",
              },
              rules: {
                ".Input": {
                  fontFamily:
                    '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                },
                ".Label": {
                  fontFamily:
                    '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                },
                ".Tab": {
                  fontFamily:
                    '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                },
                ".TermsText": {
                  fontFamily:
                    '"DM Sans", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                },
              },
            },
          }}
        >
          <AddCardForm
            clientSecret={clientSecret}
            onClose={onClose}
            onSuccess={onSuccess}
            canManageBilling={canManageBilling}
            canAddPaypal={canAddPaypal}
          />
        </Elements>
      )}

      {canAddAny && !canAddCard && canAddPaypal && (
        <>
          <DialogBody>
            <ConnectPaypalButton
              canManageBilling={canManageBilling}
              onSuccess={(newSource) => {
                if (newSource) onSuccess(newSource);
              }}
              onClose={onClose}
              layout="centered"
            />
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t(
                "PAYMENT.MAX_CARDS_REACHED",
                "You have reached the maximum number of cards to link with your account.",
              )}
            </p>
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t("ACTION.CLOSE", "Close")}
            </Button>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}
